/**
 * App.jsx - Root component
 * Premium SaaS redesign — sidebar layout, dark/light mode
 */
import React, { useState, useCallback, useEffect } from "react";
import BookingForm        from "./components/BookingForm";
import BookingsList       from "./components/BookingsList";
import StatsCards         from "./components/StatsCards";
import Analytics          from "./components/Analytics";
import ActivityLog        from "./components/ActivityLog";
import EditBookingModal   from "./components/EditBookingModal";
import Login              from "./components/Login";
import BusinessSelect     from "./components/BusinessSelect";
import PropertiesManager  from "./components/PropertiesManager";
import PropertyDashboard  from "./components/PropertyDashboard";
import Finance            from "./components/Finance";
import InvoiceModal       from "./components/InvoiceModal";
import CalendarView       from "./components/CalendarView";
import DailyMonitor       from "./components/DailyMonitor";
import JoinRequests       from "./components/JoinRequests";
import BusinessSettings   from "./components/BusinessSettings";
import Sidebar            from "./components/Sidebar";
import TopBar             from "./components/TopBar";
import { ThemeProvider }  from "./components/ThemeProvider";
import { Toast, useToast } from "./components/Toast";
import { fetchBookings, addBooking, deleteBooking } from "./utils/api";
import { bizFetch, getBizId } from "./utils/bizApi";
import "./App.css";

const BASE_URL = process.env.REACT_APP_API_URL || "";

export default function App() {
  const [bookings,        setBookings]        = useState([]);
  const [properties,      setProperties]      = useState([]);
  const [activeTab,       setActiveTab]       = useState("dashboard");
  const [loading,         setLoading]         = useState(false);
  const [fetchLoading,    setFetchLoading]    = useState(true);
  const [deleteLoading,   setDeleteLoading]   = useState(false);
  const [editBooking,     setEditBooking]     = useState(null);
  const [invoiceBooking,  setInvoiceBooking]  = useState(null);
  const [viewProperty,    setViewProperty]    = useState(null);
  const [pendingCount,      setPendingCount]      = useState(0);
  const [userPermissions,   setUserPermissions]   = useState(null);
  const [permissionsLoaded, setPermissionsLoaded] = useState(false);
  const [businessLogo,      setBusinessLogo]      = useState("");
  const [showMobileMenu,    setShowMobileMenu]    = useState(false);
  const [sidebarCollapsed,  setSidebarCollapsed]  = useState(() => localStorage.getItem("st_sidebar") === "true");

  // Persist sidebar state
  const toggleSidebar = (val) => {
    const next = typeof val === "boolean" ? val : (p) => !p;
    setSidebarCollapsed(prev => {
      const newVal = typeof next === "function" ? next(prev) : next;
      localStorage.setItem("st_sidebar", String(newVal));
      return newVal;
    });
  };

  // ── Auth ────────────────────────────────────────────────────────────────────
  const [isLoggedIn,    setIsLoggedIn]    = useState(false);
  const [currentUser,   setCurrentUser]   = useState("");
  const [currentRole,   setCurrentRole]   = useState("user");
  const [activeBusiness, setActiveBusiness] = useState(null);
  const { toasts, addToast, removeToast }   = useToast();

  // ── Restore session ─────────────────────────────────────────────────────────
  useEffect(() => {
    const token    = sessionStorage.getItem("st_token");
    const username = sessionStorage.getItem("st_username");
    const role     = sessionStorage.getItem("st_role");
    const bizRaw   = sessionStorage.getItem("st_business");
    if (token) {
      fetch(`${BASE_URL}/api/auth/verify`, {
        method: "POST", headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ token }),
      })
        .then((r) => r.json())
        .then((d) => {
          if (d.success) {
            setIsLoggedIn(true);
            setCurrentUser(d.username || username || "");
            setCurrentRole(d.role     || role     || "user");
            if (bizRaw) { try { setActiveBusiness(JSON.parse(bizRaw)); } catch {} }
          }
        })
        .catch(() => {});
    }
  }, []);

  // ── Load bookings ───────────────────────────────────────────────────────────
  // ── Single batch load — bookings + properties + logo in one request ──────────
  const loadAll = useCallback(async () => {
    setFetchLoading(true);
    try {
      const res  = await bizFetch("/api/init");
      const data = await res.json();
      if (data.success) {
        setBookings(data.bookings   || []);
        setProperties(data.properties || []);
        if (data.logo !== undefined) setBusinessLogo(data.logo);
      } else {
        addToast("Could not load data.", "error");
      }
    } catch { addToast("Could not connect to server.", "error"); }
    finally { setFetchLoading(false); }
  }, []);

  // Aliases so other handlers (handleAddBooking, handleEditSaved etc.) still work
  const loadBookings   = loadAll;
  const loadProperties = useCallback(async () => {
    try {
      const res  = await bizFetch("/api/properties");
      const data = await res.json();
      if (data.success) setProperties(data.properties || []);
    } catch {}
  }, []);

  // ── Load pending join requests count (admins only) ──────────────────────────
  const loadPendingCount = useCallback(async (bizId, role) => {
    if (!bizId || role !== "admin") return;
    try {
      const res  = await fetch(`${BASE_URL}/api/businesses/${bizId}/requests/pending`);
      const data = await res.json();
      setPendingCount(data.count || 0);
    } catch {}
  }, []);

  // ── Load user permissions (non-admins) ──────────────────────────────────────
  const loadPermissions = useCallback(async (bizId, username, userRole) => {
    if (userRole === "admin") {
      setUserPermissions({
        viewBookings: true, addBookings: true, editBookings: true,
        deleteBookings: true, manageProperties: true, viewFinance: true,
        manageExpenses: true, viewActivity: true, isAdmin: true,
      });
      setPermissionsLoaded(true);
      return;
    }
    try {
      const res  = await fetch(`${BASE_URL}/api/businesses/${bizId}/permissions/${username}`);
      const data = await res.json();
      if (data.isAdmin) {
        setUserPermissions({
          viewBookings: true, addBookings: true, editBookings: true,
          deleteBookings: true, manageProperties: true, viewFinance: true,
          manageExpenses: true, viewActivity: true, isAdmin: true,
        });
      } else {
        setUserPermissions(data.permissions ? { ...data.permissions, isAdmin: false } : {
          viewBookings: true, isAdmin: false,
        });
      }
    } catch {
      setUserPermissions({ viewBookings: true, isAdmin: false });
    } finally {
      setPermissionsLoaded(true);
    }
  }, []);

  useEffect(() => {
    if (isLoggedIn && activeBusiness) {
      loadAll(); // single batch: bookings + properties + logo
      loadPendingCount(activeBusiness.businessId, activeBusiness.userRole);
      loadPermissions(activeBusiness.businessId, currentUser, activeBusiness.userRole);
    }
  }, [isLoggedIn, activeBusiness]);

  // ── Keep-alive ping every 14 min to prevent Render free tier from sleeping ──
  useEffect(() => {
    if (!isLoggedIn) return;
    const ping = () => fetch(`${BASE_URL}/api/ping`).catch(() => {});
    ping();    const interval = setInterval(ping, 14 * 60 * 1000); // every 14 min
    return () => clearInterval(interval);
  }, [isLoggedIn]);

  // ── Poll pending count every 30s for admins ─────────────────────────────────
  useEffect(() => {
    if (!activeBusiness || activeBusiness.userRole !== "admin") return;
    const interval = setInterval(() => {
      loadPendingCount(activeBusiness.businessId, activeBusiness.userRole);
    }, 30000);
    return () => clearInterval(interval);
  }, [activeBusiness]);

  // ── Handlers ────────────────────────────────────────────────────────────────
  const handleAddBooking = async (formData, resetForm) => {
    setLoading(true);
    try {
      const result = await addBooking(formData);
      addToast(`Booking ${result.bookingId} added! 🎉`, "success");
      resetForm(); await loadBookings(); setActiveTab("dashboard");
    } catch (err) { addToast(err.message || "Failed to add booking.", "error"); }
    finally { setLoading(false); }
  };

  const handleDeleteBooking = async (bookingId) => {
    setDeleteLoading(true);
    try {
      await deleteBooking(bookingId);
      addToast("Booking deleted.", "success");
      setBookings((prev) => prev.filter((b) => b.bookingId !== bookingId));
    } catch { addToast("Failed to delete booking.", "error"); }
    finally { setDeleteLoading(false); }
  };

  const handleCompleteBooking = async (bookingId, paymentReceived = 0, booking = {}) => {
    try {
      await bizFetch(`/api/bookings/${bookingId}/status`, {
        method: "PATCH", headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ status: "Completed" }),
      });
      if (paymentReceived > 0) {
        const newAdvance   = (parseFloat(booking.advancePaid)||0) + paymentReceived;
        const newRemaining = Math.max(0, (parseFloat(booking.totalPrice)||0) - newAdvance);
        await bizFetch(`/api/bookings/${bookingId}`, {
          method: "PUT", headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ ...booking, advancePaid: newAdvance, remaining: newRemaining, status: "Completed" }),
        });
        setBookings((prev) => prev.map((b) => b.bookingId === bookingId
          ? { ...b, status: "Completed", advancePaid: newAdvance, remaining: newRemaining } : b));
        addToast(`Completed! PKR ${paymentReceived.toLocaleString()} recorded ✅`, "success");
      } else {
        setBookings((prev) => prev.map((b) => b.bookingId === bookingId ? { ...b, status: "Completed" } : b));
        addToast("Booking marked as Completed ✅", "success");
      }
    } catch { addToast("Could not complete booking.", "error"); }
  };

  const handleEditSaved = async (msg) => { addToast(msg, "success"); await loadBookings(); };

  const handleLogout = () => {
    sessionStorage.clear();
    setIsLoggedIn(false); setCurrentUser(""); setCurrentRole("user");
    setActiveBusiness(null); setBookings([]); setUserPermissions(null);
  };

  const handleSwitchBusiness = () => {
    sessionStorage.removeItem("st_business");
    sessionStorage.removeItem("st_businessId");
    setActiveBusiness(null); setBookings([]); setProperties([]);
    setUserPermissions(null); setPendingCount(0); setActiveTab("dashboard");
    setPermissionsLoaded(false);
    setBusinessLogo("");
  };

  const handleSelectBusiness = (biz) => {
    sessionStorage.setItem("st_business",   JSON.stringify(biz));
    sessionStorage.setItem("st_businessId", biz.businessId);
    setActiveBusiness(biz);
  };

  const isAdmin    = activeBusiness?.userRole === "admin";
  const perms      = userPermissions || {};

  // ── Build visible tabs based on permissions ─────────────────────────────────
  const ALL_TABS = [
    { key: "dashboard",  label: "Dashboard",    icon: "📊", show: isAdmin || perms.viewBookings },
    { key: "monitor",    label: "Daily Monitor", icon: "📡", show: isAdmin || perms.viewBookings },
    { key: "calendar",   label: "Calendar",     icon: "📅", show: isAdmin || perms.viewBookings },
    { key: "analytics",  label: "Analytics",    icon: "📈", show: isAdmin || perms.viewBookings },
    { key: "finance",    label: "Finance",      icon: "💰", show: isAdmin || perms.viewFinance  },
    { key: "properties", label: "Properties",   icon: "🏠", show: isAdmin || perms.manageProperties },
    { key: "activity",   label: "Activity Log", icon: "📜", show: isAdmin },
    { key: "requests",   label: "Join Requests",icon: "🔔", show: isAdmin }, // admin only
    { key: "settings",   label: "Settings",     icon: "⚙️",  show: isAdmin }, // admin only
    { key: "add",        label: "New Booking",  icon: "➕", show: isAdmin || perms.addBookings  },
  ];
  const TABS = ALL_TABS.filter((t) => t.show);

  const upcomingCount = bookings.filter((b) => {
    const diff = (new Date(b.checkIn) - new Date()) / (1000 * 60 * 60 * 24);
    return diff >= 0 && diff <= 3 && b.status === "Upcoming";
  }).length;
  const pendingPayCount = bookings.filter((b) => (b.remaining||0) > 0 && b.status !== "Cancelled").length;

  // ── Steps ───────────────────────────────────────────────────────────────────
  if (!isLoggedIn) {
    return (
      <ThemeProvider>
        <Toast toasts={toasts} removeToast={removeToast} />
        <Login onLogin={(token, username, role) => {
          setIsLoggedIn(true); setCurrentUser(username||""); setCurrentRole(role||"user");
        }} />
      </ThemeProvider>
    );
  }

  if (!activeBusiness) {
    return (
      <ThemeProvider>
        <Toast toasts={toasts} removeToast={removeToast} />
        <BusinessSelect username={currentUser} onSelect={handleSelectBusiness} />
      </ThemeProvider>
    );
  }

  // Show loading while fetching permissions for non-admins
  if (!permissionsLoaded) {
    return (
      <ThemeProvider>
        <div style={{ minHeight:"100vh", background:"var(--bg)", display:"flex",
          alignItems:"center", justifyContent:"center", flexDirection:"column", gap:16 }}>
          <div style={{ width:48, height:48, borderRadius:14,
            background:"linear-gradient(135deg,#F5B841,#F0A800)",
            display:"flex", alignItems:"center", justifyContent:"center", fontSize:24,
            boxShadow:"0 8px 24px rgba(245,184,65,0.35)" }}>🏠</div>
          <p style={{ color:"var(--text-2)", fontSize:14, fontFamily:"Inter,sans-serif" }}>
            Loading your workspace…
          </p>
          <div className="skeleton" style={{ width:180, height:6, borderRadius:99 }} />
        </div>
      </ThemeProvider>
    );
  }

  // ── Page content renderer ───────────────────────────────────────────────────
  const renderPage = () => {
    if (fetchLoading && ["dashboard","monitor","calendar","analytics"].includes(activeTab)) {
      return (
        <div style={{ display:"grid", gridTemplateColumns:"repeat(auto-fill,minmax(280px,1fr))", gap:16 }}>
          {[...Array(4)].map((_,i) => <div key={i} className="skeleton" style={{ height:140 }} />)}
        </div>
      );
    }

    switch (activeTab) {
      case "dashboard":
        return (
          <div className="fade-in">
            <StatsCards bookings={bookings} />
            <div className="st-card">
              <BookingsList bookings={bookings} onDelete={handleDeleteBooking}
                deleteLoading={deleteLoading}
                onEdit={(isAdmin||perms.editBookings) ? b=>setEditBooking(b) : null}
                onInvoice={b=>setInvoiceBooking(b)}
                onComplete={(isAdmin||perms.editBookings) ? handleCompleteBooking : null}
                canDelete={isAdmin||perms.deleteBookings} />
            </div>
          </div>
        );

      case "monitor":
        return (
          <div className="fade-in">
            {fetchLoading
              ? <div style={{ display:"grid", gridTemplateColumns:"repeat(auto-fill,minmax(220px,1fr))", gap:16 }}>
                  {[...Array(4)].map((_,i) => <div key={i} className="skeleton" style={{ height:220 }} />)}
                </div>
              : <DailyMonitor bookings={bookings} properties={properties} />}
          </div>
        );

      case "calendar":
        return (
          <div className="fade-in">
            {fetchLoading
              ? <div className="skeleton" style={{ height:500 }} />
              : <CalendarView bookings={bookings} onInvoice={b=>setInvoiceBooking(b)} />}
          </div>
        );

      case "analytics":
        return (
          <div className="fade-in">
            {fetchLoading
              ? <div className="skeleton" style={{ height:400 }} />
              : <Analytics bookings={bookings} />}
          </div>
        );

      case "finance":
        return (
          <div className="fade-in">
            <Finance bookings={bookings} properties={properties} />
          </div>
        );

      case "properties":
        return (
          <div className="fade-in">
            <PropertiesManager
              onViewDashboard={p=>setViewProperty(p)}
              onToast={(msg,type)=>addToast(msg,type)} />
          </div>
        );

      case "activity":
        return (
          <div className="fade-in">
            <ActivityLog />
          </div>
        );

      case "requests":
        return isAdmin ? (
          <div className="fade-in">
            <JoinRequests business={activeBusiness} currentUser={currentUser}
              onMemberAdded={msg=>{ addToast(msg,"success"); loadPendingCount(activeBusiness.businessId, activeBusiness.userRole); }} />
          </div>
        ) : null;

      case "settings":
        return isAdmin ? (
          <div className="fade-in">
            <BusinessSettings business={activeBusiness}
              onLogoUpdated={logo=>{ setBusinessLogo(logo); bizFetch("/api/init").then(r=>r.json()).then(d=>{ if(d.success&&d.logo!==undefined) setBusinessLogo(d.logo); }).catch(()=>{}); }} />
          </div>
        ) : null;

      case "add":
        return (
          <div className="fade-in" style={{ maxWidth:860 }}>
            <BookingForm onSubmit={handleAddBooking} loading={loading}
              existingBookings={bookings} dynamicProperties={properties} />
          </div>
        );

      default:
        return null;
    }
  };

  return (
    <ThemeProvider>
      <div className="app-shell">
        <Toast toasts={toasts} removeToast={removeToast} />

        {editBooking    && <EditBookingModal booking={editBooking} onClose={()=>setEditBooking(null)} onSaved={handleEditSaved} />}
        {invoiceBooking && <InvoiceModal booking={invoiceBooking} onClose={()=>setInvoiceBooking(null)}
          businessName={activeBusiness?.name||"StayTrack"} businessLogo={businessLogo} />}
        {viewProperty   && <PropertyDashboard property={viewProperty} allBookings={bookings}
          onClose={()=>setViewProperty(null)}
          onEdit={()=>{ setViewProperty(null); setActiveTab("properties"); }} />}

        {/* ── Sidebar ── */}
        <Sidebar
          activeTab={activeTab}
          setActiveTab={setActiveTab}
          collapsed={sidebarCollapsed}
          setCollapsed={toggleSidebar}
          mobileOpen={showMobileMenu}
          setMobileOpen={setShowMobileMenu}
          business={activeBusiness}
          isAdmin={isAdmin}
          pendingCount={pendingCount}
          onSwitchBusiness={handleSwitchBusiness}
        />

        {/* ── Main wrapper ── */}
        <div className={`main-wrapper${sidebarCollapsed ? " sidebar-collapsed" : ""}`}>
          {/* Top bar */}
          <TopBar
            activeTab={activeTab}
            currentUser={currentUser}
            isAdmin={isAdmin}
            pendingCount={pendingCount}
            upcomingCount={upcomingCount}
            onNewBooking={()=>setActiveTab("add")}
            onLogout={handleLogout}
            onMobileMenu={()=>setShowMobileMenu(p=>!p)}
            onSwitchBusiness={handleSwitchBusiness}
            businessName={activeBusiness?.name||"StayTrack"}
          />

          {/* No permissions */}
          {!isAdmin && userPermissions && !Object.values(perms).some(Boolean) && (
            <div style={{ padding:"80px 32px", textAlign:"center", color:"var(--text-2)" }}>
              <div style={{ fontSize:48, marginBottom:20 }}>⏳</div>
              <h2 style={{ fontSize:22, marginBottom:10, color:"var(--text)", fontFamily:"Inter" }}>
                Waiting for Access
              </h2>
              <p style={{ fontSize:14 }}>
                The admin of <strong style={{ color:"var(--gold)" }}>{activeBusiness.name}</strong> hasn't granted you permissions yet.
              </p>
            </div>
          )}

          {/* Page content */}
          {(isAdmin || Object.values(perms).some(Boolean)) && (
            <main className="page-content">
              {renderPage()}
            </main>
          )}

          {/* Footer */}
          <footer style={{ padding:"20px 28px", borderTop:"1px solid var(--border)",
            fontSize:12, color:"var(--text-3)", display:"flex",
            alignItems:"center", justifyContent:"space-between", flexWrap:"wrap", gap:8 }}>
            <span>{activeBusiness?.name} · StayTrack</span>
            <span>Built for Airbnb hosts</span>
          </footer>
        </div>
      </div>
    </ThemeProvider>
  );
}
